import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { fetchECACReport, resetECACReport, selectECACReportData } from 'redux/slices/sla-reports/ecac-report';
import { fetchExternalCallPointReport, resetExternalCallPointReport, selectExternalCallPointReportData } from 'redux/slices/sla-reports/external-call-point-report';
import { fetchBoardingSla, resetBoardingSla, selectBoardingSlaData } from 'redux/slices/sla-reports/boarding-sla';
import { fetchTransferSla, resetTransferSla, selectTransferSlaData } from 'redux/slices/sla-reports/transfer-sla';
import { fetchInboundSla, resetInboundSla, selectInboundSlaData } from 'redux/slices/sla-reports/inbound-sla';
import { fetchTerminalReceptionReport, resetTerminalReception, selectTerminalReceptionData } from 'redux/slices/sla-reports/terminal-reception';
import TerminalReceptionSummary from './terminal-reception-summary';
import BoardingSlaSummary from './boarding-sla-summary';
import TransferSlaSummary from './transfer-sla-summary';
import InboundSlaSummary from './inbound-sla-summary';

// Summary view only renders KPI scoreboards, never the per-passenger rawData
// tables, so we ask the backend to drop rawData via hideBreakdown=true. That
// shrinks each response by 10-100x (the slow part for big date ranges) and
// avoids freezing React while it accepts the giant payloads into Redux.
const SUB_REPORTS = [
    {
        key: 'ecac',
        label: 'Terminal Reception',
        thunk: fetchECACReport,
        reset: resetECACReport,
        buildParams: (base) => ({ ...base, format: 'kpi', hideBreakdown: true }),
    },
    {
        key: 'external-call-point',
        label: 'External Call Point',
        thunk: fetchExternalCallPointReport,
        reset: resetExternalCallPointReport,
        buildParams: (base) => ({ ...base, hideBreakdown: true }),
    },
    {
        key: 'boarding-sla',
        label: 'Boardings',
        thunk: fetchBoardingSla,
        reset: resetBoardingSla,
        buildParams: (base) => ({ ...base, hideBreakdown: true }),
    },
    {
        key: 'transfer-sla',
        label: 'Transfer SLA',
        thunk: fetchTransferSla,
        reset: resetTransferSla,
        buildParams: (base) => ({ ...base, hideBreakdown: true }),
    },
    {
        key: 'inbound-sla',
        label: 'Inbounds',
        thunk: fetchInboundSla,
        reset: resetInboundSla,
        buildParams: (base) => ({ ...base, hideBreakdown: true }),
    },
    {
        key: 'terminal-reception',
        label: 'Terminal Holding Points (218/407)',
        thunk: fetchTerminalReceptionReport,
        reset: resetTerminalReception,
        buildParams: (base) => ({ ...base, hideBreakdown: true }),
    },
];

const MAX_DAYS = 32;

const toLocalDateString = (d) => {
    const dt = new Date(d);
    return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
};

const SlaSummaryReport = ({ dateRange, iata, runReportRef, onRunLoadingChange }) => {
    const dispatch = useDispatch();
    const cancelledRef = useRef(false);

    const [currentIndex, setCurrentIndex] = useState(null);
    const [errors, setErrors] = useState({});
    const [done, setDone] = useState(false);
    const [completedKeys, setCompletedKeys] = useState({});

    const ecacData = useSelector(selectECACReportData);
    const extCallPointData = useSelector(selectExternalCallPointReportData);
    const boardingData = useSelector(selectBoardingSlaData);
    const transferData = useSelector(selectTransferSlaData);
    const inboundData = useSelector(selectInboundSlaData);
    const termReceptionData = useSelector(selectTerminalReceptionData);

    const dataByKey = {
        'ecac': ecacData,
        'external-call-point': extCallPointData,
        'boarding-sla': boardingData,
        'transfer-sla': transferData,
        'inbound-sla': inboundData,
        'terminal-reception': termReceptionData,
    };

    const isRunning = currentIndex !== null;

    const handleRunReport = useCallback(async () => {
        const startDate = dateRange?.[0];
        const endDate = dateRange?.[1];
        if (!startDate || !endDate || !iata) return;

        const diffMs = new Date(endDate) - new Date(startDate);
        const diffDays = diffMs / (1000 * 60 * 60 * 24);
        if (diffDays > MAX_DAYS) {
            alert(`The SLA Summary Report can only be run for up to ${MAX_DAYS} days at a time. Please select a shorter date range.`);
            return;
        }

        const formattedStartDate = toLocalDateString(startDate);
        const formattedEndDate = toLocalDateString(endDate);
        const baseParams = { startDate: formattedStartDate, endDate: formattedEndDate, iata };

        cancelledRef.current = false;
        setErrors({});
        setDone(false);
        setCompletedKeys({});

        // Run sub-reports one after another to keep load on the cpu service
        // (and its downstream flights / Firestore / PRM API dependencies)
        // smooth. With hideBreakdown=true each response is tiny, so the
        // sequential wall time is acceptable.
        for (let i = 0; i < SUB_REPORTS.length; i++) {
            if (cancelledRef.current) return;
            setCurrentIndex(i);
            const report = SUB_REPORTS[i];
            try {
                await dispatch(report.thunk(report.buildParams(baseParams))).unwrap();
                setCompletedKeys((prev) => ({ ...prev, [report.key]: true }));
            } catch (e) {
                setErrors((prev) => ({ ...prev, [report.key]: e?.message ?? 'Failed' }));
                setCompletedKeys((prev) => ({ ...prev, [report.key]: true }));
            }
        }

        if (!cancelledRef.current) {
            setCurrentIndex(null);
            setDone(true);
        }
    }, [dispatch, dateRange, iata]);

    useEffect(() => {
        if (runReportRef) runReportRef.current = handleRunReport;
        return () => { if (runReportRef) runReportRef.current = null; };
    }, [handleRunReport, runReportRef]);

    useEffect(() => {
        if (onRunLoadingChange) onRunLoadingChange(isRunning);
    }, [isRunning, onRunLoadingChange]);

    useEffect(() => {
        return () => {
            cancelledRef.current = true;
            SUB_REPORTS.forEach((r) => dispatch(r.reset()));
        };
    }, [dispatch]);

    const renderSummary = (key, data) => {
        if (!data) return null;
        const dailyReports = data.dailyReports ?? [];

        switch (key) {
            case 'ecac':
            case 'external-call-point':
            case 'terminal-reception':
                return (
                    <TerminalReceptionSummary
                        dailyReports={dailyReports}
                        overallSummary={data.summary}
                    />
                );
            case 'boarding-sla':
                return (
                    <BoardingSlaSummary
                        dailyReports={dailyReports}
                        overallSummary={data.summary}
                        includeNoData={false}
                        hideDailyBreakdown={true}
                    />
                );
            case 'transfer-sla':
                return (
                    <TransferSlaSummary
                        dailyReports={dailyReports}
                        overallSummary={data.summary}
                        hideDailyBreakdown={true}
                    />
                );
            case 'inbound-sla':
                return (
                    <InboundSlaSummary
                        dailyReports={dailyReports}
                        overallSummary={data.summary}
                        includeNoData={false}
                        hideDailyBreakdown={true}
                    />
                );
            default:
                return null;
        }
    };

    const progressPercent = isRunning
        ? Math.round(((currentIndex + 1) / SUB_REPORTS.length) * 100)
        : done ? 100 : 0;

    return (
        <div>
            {/* Progress card while running */}
            {(isRunning || (!done && progressPercent === 0)) && (
                <div style={{
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    padding: '20px',
                    marginBottom: '24px',
                    backgroundColor: '#f9fafb',
                }}>
                    {isRunning && (
                        <>
                            <p style={{ fontSize: '14px', fontWeight: 500, color: '#1f2937', marginTop: 0, marginBottom: '12px' }}>
                                Fetching report {currentIndex + 1} of {SUB_REPORTS.length}: {SUB_REPORTS[currentIndex].label}
                            </p>
                            <div style={{
                                width: '100%',
                                height: '8px',
                                backgroundColor: '#e5e7eb',
                                borderRadius: '4px',
                                overflow: 'hidden',
                                marginBottom: '16px',
                            }}>
                                <div style={{
                                    width: `${progressPercent}%`,
                                    height: '100%',
                                    backgroundColor: '#3b82f6',
                                    borderRadius: '4px',
                                    transition: 'width 0.3s ease',
                                }} />
                            </div>
                        </>
                    )}
                    <style>{`
                        @keyframes sla-spin {
                            to { transform: rotate(360deg); }
                        }
                    `}</style>
                    <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                        {SUB_REPORTS.map((report, idx) => {
                            let status = 'pending';
                            if (errors[report.key]) status = 'error';
                            else if (completedKeys[report.key] && !errors[report.key]) status = 'done';
                            else if (isRunning && idx === currentIndex) status = 'loading';

                            const color = {
                                pending: '#9ca3af',
                                loading: '#3b82f6',
                                done: '#16a34a',
                                error: '#dc2626',
                            }[status];

                            const iconEl = status === 'loading' ? (
                                <span style={{
                                    display: 'inline-block',
                                    width: '14px',
                                    height: '14px',
                                    border: '2px solid #bfdbfe',
                                    borderTopColor: '#3b82f6',
                                    borderRadius: '50%',
                                    animation: 'sla-spin 0.7s linear infinite',
                                    flexShrink: 0,
                                }} />
                            ) : (
                                <span style={{ fontSize: '14px', width: '16px', textAlign: 'center', flexShrink: 0 }}>
                                    {{ pending: '\u25CB', done: '\u2713', error: '\u2717' }[status]}
                                </span>
                            );

                            return (
                                <li key={report.key} style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    padding: '4px 0',
                                    fontSize: '13px',
                                    color,
                                    fontWeight: status === 'loading' ? 600 : 400,
                                }}>
                                    {iconEl}
                                    {report.label}
                                    {errors[report.key] && (
                                        <span style={{ fontSize: '12px', color: '#dc2626', marginLeft: '4px' }}>
                                            — {errors[report.key]}
                                        </span>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                </div>
            )}

            {/* Stacked summary sections after done */}
            {done && SUB_REPORTS.map((report) => {
                const data = dataByKey[report.key];
                const hasError = !!errors[report.key];

                return (
                    <div key={report.key} style={{ marginBottom: '32px' }}>
                        <h3 style={{
                            fontSize: '15px',
                            fontWeight: 600,
                            color: '#1f2937',
                            marginBottom: '12px',
                            paddingBottom: '8px',
                            borderBottom: '2px solid #e5e7eb',
                        }}>
                            {report.label}
                        </h3>
                        {hasError ? (
                            <p style={{ fontSize: '13px', color: '#dc2626' }}>
                                Failed to load: {errors[report.key]}
                            </p>
                        ) : data ? (
                            renderSummary(report.key, data)
                        ) : (
                            <p style={{ fontSize: '13px', color: '#6b7280' }}>No data available.</p>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

export default SlaSummaryReport;
